import React from 'react';
import {
  Box,
  Grid,
  Heading,
  HStack,
  Icon,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiInfo } from 'react-icons/fi';

/**
 * Read-only "Imported Data" card rendering a record's extra_fields — the
 * spreadsheet columns that had no matching Origin field at import time.
 * Keys are the original CSV header names. Renders nothing when empty.
 *
 * `bare` renders just the key/value grid (for embedding in an existing
 * card/accordion); default renders a heading + card to match detail views.
 */
export default function ImportedDataCard({ extraFields, bare = false, ...boxProps }) {
  const infoBG = useColorModeValue('gray.50', 'gray.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const entries = Object.entries(extraFields || {}).filter(
    ([, value]) => value !== null && value !== undefined && String(value).trim() !== ''
  );
  if (entries.length === 0) return null;

  const grid = (
    <Grid templateColumns="repeat(auto-fill, minmax(170px, 1fr))" gap={3} fontSize="sm">
      {entries.map(([key, value]) => (
        <Box key={key} minW={0}>
          <Text fontSize="xs" fontWeight="semibold" color={labelColor} isTruncated title={key}>
            {key}
          </Text>
          <Text wordBreak="break-word">
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </Text>
        </Box>
      ))}
    </Grid>
  );

  if (bare) {
    return <Box {...boxProps}>{grid}</Box>;
  }

  return (
    <Box {...boxProps}>
      <HStack mb={3} spacing={2}>
        <Heading size="sm">Imported Data</Heading>
        <Tooltip
          label="Extra spreadsheet columns from the original import that don't have a standard Origin field — kept here so nothing from the file is lost."
          hasArrow
        >
          <span>
            <Icon as={FiInfo} boxSize={3.5} color="gray.400" display="block" />
          </span>
        </Tooltip>
      </HStack>
      <Box p={4} bg={infoBG} borderRadius="md">
        {grid}
      </Box>
    </Box>
  );
}
